import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { SKILLSVIEW_LOGO_SRC } from "@/assets/brandImages";
import { Menu, X, User, LogOut } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";


const LogoMark = ({ className = "" }) => (
  <img
    src={SKILLSVIEW_LOGO_SRC}
    alt="SkillsView"
    className={`h-10 w-auto object-contain ${className}`}
  />
);

const LanguageToggle = ({ compact = false }) => {
  const { language, setLanguage } = useLanguage();

  return (
    <div className={`inline-flex items-center border border-white/10 bg-white/5 ${compact ? "w-fit" : ""}`}>
      {["fr", "en"].map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLanguage(code)}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
            language === code
              ? "bg-[#CCFF00] text-black"
              : "text-zinc-400 hover:text-white"
          }`}
          aria-pressed={language === code}
        >
          {code}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <LogoMark className="max-w-[168px]" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/#comment-ca-marche" className="text-sm text-zinc-400 hover:text-white transition-colors">
              Comment ça marche
            </Link>
            <Link to="/pricing" className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid="nav-pricing">
              Tarifs
            </Link>
            <LanguageToggle />
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-sm text-white hover:text-[#CCFF00] transition-colors" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                    <span>{user.first_name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#18181B] border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer" data-testid="nav-dashboard">
                      Espace membre
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="nav-admin">
                        Espace admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-400" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-sm text-white hover:text-[#CCFF00] transition-colors" data-testid="nav-login">
                  Connexion
                </Link>
                <Link 
                  to="/register" 
                  className="bg-[#CCFF00] text-black px-4 py-2 text-sm font-bold uppercase tracking-wide hover:bg-[#AACC00] transition-colors"
                  data-testid="nav-register"
                >
                  S'inscrire
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0A0A0A] border-t border-white/10">
          <nav className="flex flex-col p-4 gap-4">
            <Link to="/#comment-ca-marche" className="text-zinc-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
              Comment ça marche
            </Link>
            <Link to="/pricing" className="text-zinc-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
              Tarifs
            </Link>
            <LanguageToggle compact />
            {user ? (
              <>
                <Link to="/dashboard" className="text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                  Espace membre
                </Link>
                {user?.role === "admin" && (
                  <Link to="/admin" className="text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Espace admin
                  </Link>
                )}
                <button onClick={handleLogout} className="text-red-400 text-left py-2">
                  Déconnexion
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                  Connexion
                </Link>
                <Link to="/register" className="bg-[#CCFF00] text-black px-4 py-2 font-bold text-center" onClick={() => setMobileMenuOpen(false)}>
                  S'inscrire
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] border-t border-white/10 py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <LogoMark className="h-11 max-w-[180px]" />
            </div>
            <p className="text-sm text-zinc-500">
              Analyses vidéo tactiques et techniques pour clubs et joueurs de football.
            </p>
          </div>
          
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-wider">Navigation</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-sm text-zinc-500 hover:text-white transition-colors">Accueil</Link></li>
              <li><Link to="/pricing" className="text-sm text-zinc-500 hover:text-white transition-colors">Tarifs</Link></li>
              <li><Link to="/login" className="text-sm text-zinc-500 hover:text-white transition-colors">Connexion</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-wider">Légal</h4>
            <ul className="space-y-2">
              <li><Link to="/mentions-legales" className="text-sm text-zinc-500 hover:text-white transition-colors">Mentions légales</Link></li>
              <li><Link to="/politique-confidentialite" className="text-sm text-zinc-500 hover:text-white transition-colors">Politique de confidentialité</Link></li>
              <li><Link to="/cgv" className="text-sm text-zinc-500 hover:text-white transition-colors">CGV</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-wider">Contact</h4>
            <p className="text-sm text-zinc-500">skillsview@ikmail.com</p>
            <p className="text-sm text-zinc-500 mt-2">06 47 56 03 68 (WhatsApp)</p>
            <a 
              href="https://wa.me/message/CPZJFHPCCSBMH1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 text-sm text-green-400 hover:text-green-300"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Nous contacter
            </a>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-8 pt-8 text-center">
          <p className="text-sm text-zinc-600">© 2025 Mathis El Seghetto — SIRET 993 849 827</p>
        </div>
      </div>
    </footer>
  );
};
